const fontWeightBold = 'var(--fontWeightBold, 700)'
const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { Button } from '@calendly/ui/components/button'
import styled from 'astroturf/react'
import { OnetrustSettingsButton } from '@calendly/onetrust/redux'
import { MainPanel } from '@calendly/booking-kit'
import { TitleText } from '@calendly/ui/components/typography'
import { FlexLayout } from '@calendly/ui/components/flex-layout'

import Segment from 'services/analytics/segment'
import { useI18n } from 'locales'
import CanceledByField from 'components/canceled_by_field'
import Container from 'components/layout/container'
import ContentWrapper from 'components/layout/content_wrapper'
import TextField from 'components/form/text'
import DetailsForm from 'components/details_form'
import { cancellation } from 'actions/cancellation'
import { isSubmittingSelector } from 'selectors/cancellation'
import {
  canceledByRequiredSelector,
  eventSelector
} from 'selectors'
import { uuidSelector, nameSelector } from 'selectors/invitee'
import { chargeCapturedSelector } from 'selectors/payment'
import { useEventType } from 'components/use_event_type'

import SidePanel from './side_panel'
import PaymentInfo from './payment_info'
import _CancelReasonField from "form-CancelReasonField.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/cancellation/form/form.js?styleId=CancelReasonField";
import _CancellationPolicy from "form-CancellationPolicy.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/cancellation/form/form.js?styleId=CancellationPolicy";
import _StyledCanceledByField from "form-StyledCanceledByField.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/cancellation/form/form.js?styleId=StyledCanceledByField";
import _CancellationGuestComment from "form-CancellationGuestComment.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/cancellation/form/form.js?styleId=CancellationGuestComment";
import _CookieSettingsButton from "form-CookieSettingsButton.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/cancellation/form/form.js?styleId=CookieSettingsButton";


const CancelReasonField = /*#__PURE__*/styled(TextField, null, {
  displayName: "CancelReasonField",
  styles: _CancelReasonField
})

const CancellationPolicy = /*#__PURE__*/styled('p', null, {
  displayName: "CancellationPolicy",
  styles: _CancellationPolicy
})

const StyledCanceledByField = /*#__PURE__*/styled(CanceledByField, null, {
  displayName: "StyledCanceledByField",
  styles: _StyledCanceledByField
})

const CancellationGuestComment = /*#__PURE__*/styled('small', null, {
  displayName: "CancellationGuestComment",
  styles: _CancellationGuestComment
})

const CookieSettingsButton = /*#__PURE__*/styled(OnetrustSettingsButton, null, {
  displayName: "CookieSettingsButton",
  styles: _CookieSettingsButton
})

const buildInviteeAttribute = (attribute, required, value) => (
  {
    name: attribute,
    required,
    value
  }
)

const Form = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const isSubmitting = useSelector(isSubmittingSelector)
  const event = useSelector(eventSelector)
  const cancellationPolicy = event.cancellation_policy
  const inviteeUUID = useSelector(uuidSelector)
  const inviteeName = useSelector(nameSelector)
  const canceledByRequired = useSelector(canceledByRequiredSelector)
  const showPaymentInfo = useSelector(chargeCapturedSelector)
  const isTabletDown = useDeviceType('isTabletDown')
  const [cancelReason, setCancelReason] = useState('')
  const [canceledBy, setCanceledBy] = useState('')
  const eventType = useEventType()

  const buildValidationData = () => {
    const inviteeCancelReason = buildInviteeAttribute('cancel_reason', false, cancelReason)
    const inviteeCanceledBy = buildInviteeAttribute('canceled_by', canceledByRequired, canceledBy)

    return {
      invitee: {
        cancel_reason: inviteeCancelReason,
        canceled_by: inviteeCanceledBy
      },
      event: [],
      event_fields: []
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const success = await dispatch(cancellation(inviteeUUID, {
      cancel_reason: cancelReason,
      canceled_by: canceledBy
    }, buildValidationData()))

    if (success) {
      // Segment v1 (device-mode) for Braze
      Segment.track('Invitee Canceled', {
        event_uuid: event.uuid,
        event_type_id: eventType?.id,
        actor_type: 'user',
        did_reschedule: false,
      })
    }
  }
  const onReasonChange = ({ target: { value } }) => setCancelReason(value)
  const onCanceledByChange = ({ target: { value } }) => setCanceledBy(value)

  return (
    <Container expanded>
      <SidePanel />
      <MainPanel>
        <ContentWrapper>
          <DetailsForm>
            <FlexLayout direction="column" gap={20}>
              <TitleText level={2} size="large">
                {i18n.t('booking.templates.cancel_event_question')}
              </TitleText>
              {showPaymentInfo && <PaymentInfo />}
              <form
                autoComplete="off"
                autoCorrect="off"
                noValidate
                onSubmit={onSubmit}
              >
                {canceledByRequired && (
                  <StyledCanceledByField onChange={onCanceledByChange} name="canceled_by" />
                )}
                <CancelReasonField
                  label={i18n.t('booking.templates.cancel_event_reason_label')}
                  name="cancel_reason"
                  type="textarea"
                  onChange={onReasonChange}
                  hasCancellationPolicy={!!cancellationPolicy}
                />
                {cancellationPolicy && (
                  <CancellationPolicy>{cancellationPolicy}</CancellationPolicy>
                )}
                <Button
                  type="submit"
                  size="large"
                  loading={isSubmitting}
                >
                  {i18n.t('booking.templates.cancel_event')}
                </Button>
                {event.guests.length > 0 && (
                  <CancellationGuestComment>
                    {i18n.t('booking.templates.cancellation_guests_comment', {
                      count: event.guests.length,
                      invitee_name: inviteeName
                    })}
                  </CancellationGuestComment>
                )}
              </form>
            </FlexLayout>
          </DetailsForm>
          {isTabletDown && <CookieSettingsButton />}
        </ContentWrapper>
      </MainPanel>
    </Container>
  )
}

export default Form
